import React, { useState } from 'react';
import { Check, X, Trash2, AlertCircle, ChevronDown, ChevronUp, Sparkles, Zap, Loader, RefreshCw } from 'lucide-react';
import { correctionsApi } from '../api/corrections';
import ConfidenceBadge from './ConfidenceBadge';
import CorrectionImpactDisplay from './CorrectionImpactDisplay';
import FeedbackButtons from './FeedbackButtons';
import { Entity } from '../types';
import { config } from '../utils/config';
import { friendlyFieldName } from '../utils/entityLabels';
import { useConfirm } from './ui/ConfirmDialog';

interface EntityCorrectionWidgetProps {
  entity: Entity;
  onCorrectionSubmitted?: () => void;
  showConfidence?: boolean;
  documentId?: number;  // NEW: Required for AI re-extraction
}

interface ExtractFieldResponse {
  extracted_value: string | null;
  confidence: number;
  reasoning: string | null;
  changed: boolean;
  suggested_label?: string | null;
  error: string | null;
  metadata: any;
}

/**
 * EntityCorrectionWidget - Smart correction interface that routes to appropriate system
 *
 * Routes corrections based on extraction method:
 * - Hybrid extraction → /api/corrections (learning system)
 * - Template extraction → /api/feedback/entities (template learning)
 * - Legacy → /api/feedback/entities (fallback)
 */
const EntityCorrectionWidget: React.FC<EntityCorrectionWidgetProps> = ({
  entity,
  onCorrectionSubmitted,
  showConfidence = true,
  documentId
}) => {
  const confirm = useConfirm();
  const [isEditing, setIsEditing] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [correctedText, setCorrectedText] = useState(entity.text);
  const [correctedLabel, setCorrectedLabel] = useState(entity.label);
  const [correctionReason, setCorrectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [correctionStats, setCorrectionStats] = useState<any>(null);
  const [learningApplied, setLearningApplied] = useState(false);

  // AI Re-extraction state
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState<ExtractFieldResponse | null>(null);
  const [showExtractionResult, setShowExtractionResult] = useState(false);

  // extraction_method used to drive CorrectionImpactDisplay's "Template updated"
  // claim, which was never true — every correction goes to /api/corrections and
  // touches no template. Nothing reads it now.

  // Comprehensive entity labels organized by category
  const entityLabelsByCategory = {
    'Invoice Details': [
      'INVOICE_NUMBER',
      'INVOICE_DATE',
      'DUE_DATE',
      'PO_NUMBER',
      'REFERENCE_NUMBER'
    ],
    'Parties': [
      'SELLER_NAME',
      'SELLER_ADDRESS',
      'SELLER_EMAIL',
      'SELLER_PHONE',
      'SELLER_VAT_ID',
      'SELLER_TAX_ID',
      'BUYER_NAME',
      'BUYER_ADDRESS',
      'BUYER_EMAIL',
      'BUYER_PHONE',
      'BUYER_VAT_ID',
      'BUYER_TAX_ID'
    ],
    'Financial': [
      'TOTAL_AMOUNT',
      'SUBTOTAL',
      'TAX_AMOUNT',
      'DISCOUNT',
      'PAYMENT_TERMS',
      'PAYMENT_METHOD',
      'CURRENCY'
    ],
    'Shipping & Additional': [
      'SHIPPING_COST',
      'NOTES'
    ],
    'Other': [
      'LINE_ITEM',
      'PRODUCT',
      'QUANTITY',
      'UNIT_PRICE',
      'OTHER'
    ]
  };

  // Helper to determine field type from label
  const getFieldType = (label: string): string => {
    const typeMap: Record<string, string> = {
      'INVOICE_NUMBER': 'string',
      'INVOICE_DATE': 'date',
      'DUE_DATE': 'date',
      'TOTAL_AMOUNT': 'currency',
      'TAX_AMOUNT': 'currency',
      'SUBTOTAL': 'currency',
      'SELLER_NAME': 'string',
      'BUYER_NAME': 'string',
      'SELLER_ADDRESS': 'string',
      'BUYER_ADDRESS': 'string',
      'PAYMENT_TERMS': 'string',
      'PO_NUMBER': 'string',
      'CURRENCY': 'string',
      'DISCOUNT': 'currency',
      'SHIPPING_COST': 'currency'
    };
    return typeMap[label] || 'string';
  };

  // AI Re-extraction handler
  const handleAIExtract = async () => {
    if (!documentId) {
      setError('Document ID required for AI extraction');
      return;
    }

    setIsExtracting(true);
    setExtractionResult(null);
    setShowExtractionResult(false);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(`${config.API_URL}${config.endpoints.aiExtractField}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          document_id: documentId,
          field_name: entity.label,
          field_type: getFieldType(entity.label),
          current_value: entity.text,
          surrounding_text: entity.context || undefined,
          entity_id: entity.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'AI extraction failed');
      }

      const result: ExtractFieldResponse = await response.json();
      setExtractionResult(result);
      setShowExtractionResult(true);

      // If the value was updated, refresh the list. (Reclassify/remove refresh
      // when the user picks an action.)
      if (result.changed && result.extracted_value && onCorrectionSubmitted) {
        onCorrectionSubmitted();
      }
    } catch (err) {
      console.error('AI extraction error:', err);
      setExtractionResult({
        extracted_value: null,
        confidence: 0,
        reasoning: null,
        changed: false,
        error: err instanceof Error ? err.message : 'AI extraction failed',
        metadata: null
      });
      setShowExtractionResult(true);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSubmitCorrection = async () => {
    if (!correctedText.trim()) {
      setError('Corrected text cannot be empty');
      return;
    }

    if (!entity?.id || typeof entity.id !== 'number') {
      setError('Invalid entity: missing ID');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // ALL documents now use hybrid corrections API (4-layer pipeline)
      // This feeds the learning system and improves accuracy
      const response = await correctionsApi.submitHybridCorrection({
        entity_id: entity.id,
        corrected_value: correctedText,
        field_type: correctedLabel  // may differ from entity.label = a reclassification
      });

      // The backend reports whether the correction was actually attached to a
      // vendor pattern. Only claim the system learned something when it did.
      setLearningApplied(!!response?.learning_applied);

      // Get updated learning stats
      try {
        const stats = await correctionsApi.getHybridLearningStats();
        setCorrectionStats(stats);
      } catch (err) {
        // Don't fail if stats fetch fails
        console.warn('Failed to fetch learning stats:', err);
      }

      setSuccess(true);
      setIsEditing(false);

      // Auto-hide success message after 4 seconds
      setTimeout(() => {
        setSuccess(false);
        setCorrectionStats(null);
      }, 4000);

      // Notify parent component
      if (onCorrectionSubmitted) {
        onCorrectionSubmitted();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit correction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEntity = async () => {
    const ok = await confirm({
      title: 'Delete this extracted field?',
      message: 'It will be removed from this document. This cannot be undone.',
      confirmLabel: 'Delete field',
      destructive: true,
    });
    if (!ok) return;

    if (!entity?.id || typeof entity.id !== 'number') {
      setError('Invalid entity: missing ID');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await correctionsApi.deleteEntity(entity.id);

      // Notify parent to refresh the entity list
      if (onCorrectionSubmitted) {
        onCorrectionSubmitted();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entity');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Move the current value to a different field label (keeps the value).
  const handleReclassify = async (newLabel: string) => {
    if (!entity?.id || typeof entity.id !== 'number') {
      setError('Invalid entity: missing ID');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await correctionsApi.submitHybridCorrection({
        entity_id: entity.id,
        corrected_value: entity.text,
        field_type: newLabel
      });
      setShowExtractionResult(false);
      if (onCorrectionSubmitted) {
        onCorrectionSubmitted();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reclassify');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCorrectedText(entity.text);
    setCorrectedLabel(entity.label);
    setCorrectionReason('');
    setError(null);
    setIsEditing(false);
  };

  // Highlight value in context
  const highlightValueInContext = (context: string, value: string) => {
    if (!context || !value) return context;

    const parts = context.split(new RegExp(`(${value})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === value.toLowerCase() ? (
        <span key={index} className="bg-yellow-500/30 text-yellow-200 px-1 rounded">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // Success state with CorrectionImpactDisplay component
  if (success) {
    return (
      <CorrectionImpactDisplay
        originalValue={entity.text}
        correctedValue={correctedText}
        originalConfidence={entity.confidence}
        entityLabel={entity.label}
        learningApplied={learningApplied}
        correctionStats={correctionStats}
      />
    );
  }

  // View mode (not editing)
  if (!isEditing) {
    return (
      <div className="group p-3 rounded-lg bg-coral-500/5 border border-coral-500/20 hover:border-coral-500/40 transition-all">
        {/* Main entity display */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-foreground font-medium break-all">{entity.text}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-coral-500/20 text-foreground-muted shrink-0">
                {friendlyFieldName(entity.label)}
              </span>
              {showConfidence && entity.confidence !== undefined && (
                <ConfidenceBadge confidence={entity.confidence} size="sm" />
              )}

              {/* Extraction method indicators */}
              {entity.source === 'hybrid_extraction' && (
                <span className="text-xs text-blue-400/70 shrink-0" title="Hybrid extraction">
                  <Zap className="w-3 h-3 inline" />
                </span>
              )}
              {entity.source === 'claude_validation' && (
                <span className="text-xs text-purple-600/70 dark:text-purple-400/70 shrink-0" title="AI validated">
                  <Sparkles className="w-3 h-3 inline" />
                </span>
              )}
            </div>

            {/* Context toggle */}
            {entity.context && (
              <button
                onClick={() => setShowContext(!showContext)}
                className="text-xs text-foreground-muted hover:text-foreground-secondary flex items-center gap-1 mt-1"
              >
                {showContext ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
                {showContext ? 'Hide' : 'Show'} context
              </button>
            )}

            {/* Correction metadata (if previously corrected) */}
            {entity.entity_metadata?.corrected && (
              <div className="mt-2 flex items-center gap-2">
                <Check className="w-3 h-3 text-green-400" />
                <span className="text-xs text-green-300">
                  Corrected • System learned from this
                </span>
                {entity.entity_metadata.original_value && (
                  <span className="text-xs text-foreground-muted/90">
                    (was: "{entity.entity_metadata.original_value}")
                  </span>
                )}
              </div>
            )}

            {/* Template validation warnings */}
            {entity.entity_metadata?.template_validation_failed && (
              <div className="mt-2 flex items-center gap-2 text-yellow-400">
                <AlertCircle className="w-3 h-3" />
                <span className="text-xs">
                  Template validation failed
                </span>
              </div>
            )}

            {entity.entity_metadata?.required_but_missing && (
              <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs">
                <div className="flex items-center gap-2 text-red-300">
                  <AlertCircle className="w-3 h-3" />
                  <span className="font-medium">Required field missing from template</span>
                </div>
                <p className="text-red-200/70 mt-1 text-xs">
                  This field is required by the template but was not extracted
                </p>
              </div>
            )}
          </div>

          {/* Feedback & Action buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {/* AI Re-extraction Button - "Ask AI" feature (doesn't count against AI queries) */}
            {documentId && (
              <button
                onClick={handleAIExtract}
                disabled={isExtracting}
                className={`min-h-[44px] px-3 flex items-center gap-1.5 rounded-lg transition-colors ${
                  isExtracting
                    ? 'text-purple-500 bg-purple-500/10 cursor-wait'
                    : 'text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-500/20'
                }`}
                title="Ask AI to re-extract this field"
                aria-label="Ask AI to re-extract this field"
              >
                {isExtracting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span className="text-xs font-medium">Ask AI</span>
                  </>
                )}
              </button>
            )}

            {/* Always visible Feedback Buttons (Thumbs Up/Down) */}
            <FeedbackButtons
              entity={entity}
              onFeedbackSubmitted={onCorrectionSubmitted}
              onNegativeFeedback={() => setIsEditing(true)}  // Thumbs down triggers edit mode
            />

            {/* Delete button - show on hover */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={handleDeleteEntity}
                disabled={isSubmitting}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors disabled:opacity-50"
                title="Delete entity"
                aria-label="Delete entity"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Context display (expandable) */}
        {showContext && entity.context && (
          <div className="mt-3 pt-3 border-t border-coral-500/20">
            <p className="text-xs text-foreground-muted mb-1">
              Context from document:
            </p>
            <div className="p-2 bg-coral-900/20 rounded text-xs text-foreground-secondary font-mono overflow-x-auto max-w-full">
              {highlightValueInContext(entity.context, entity.text)}
            </div>
          </div>
        )}

        {/* AI Extraction Result */}
        {showExtractionResult && extractionResult && (
          <div className={`mt-3 pt-3 border-t ${
            extractionResult.error
              ? 'border-red-500/20 bg-red-500/5'
              : extractionResult.changed
              ? 'border-green-500/20 bg-green-500/5'
              : 'border-coral-500/20 bg-coral-500/5'
          } rounded-lg p-3`}>
            {extractionResult.error ? (
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span className="text-sm">{extractionResult.error}</span>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-purple-600 dark:text-purple-400 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI Re-extraction Result
                  </span>
                  <button
                    onClick={() => setShowExtractionResult(false)}
                    className="text-coral-400 hover:text-foreground text-xs"
                  >
                    Dismiss
                  </button>
                </div>
                {extractionResult.changed && extractionResult.extracted_value ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-green-400 shrink-0" />
                      <span className="text-sm text-green-400">Value updated</span>
                    </div>
                    <p className="text-sm">
                      <span className="text-coral-400">New value: </span>
                      <span className="text-foreground font-medium">{extractionResult.extracted_value}</span>
                    </p>
                    <p className="text-xs text-coral-400">
                      Confidence: {(extractionResult.confidence * 100).toFixed(1)}%
                    </p>
                  </div>
                ) : extractionResult.suggested_label && !extractionResult.extracted_value ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                      <span className="text-sm text-amber-400">
                        This doesn't look like a {friendlyFieldName(entity.label).toLowerCase()} — it looks like the {friendlyFieldName(extractionResult.suggested_label).toLowerCase()}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleReclassify(extractionResult.suggested_label!)}
                        disabled={isSubmitting}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 border border-green-500/30 disabled:opacity-50"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Move to {friendlyFieldName(extractionResult.suggested_label)}
                      </button>
                      <button
                        onClick={handleDeleteEntity}
                        disabled={isSubmitting}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-red-500/10 text-red-500 dark:text-red-400 hover:bg-red-500/20 border border-red-500/30 disabled:opacity-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove
                      </button>
                    </div>
                  </div>
                ) : !extractionResult.extracted_value ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                      <span className="text-sm text-amber-400">
                        No {friendlyFieldName(entity.label).toLowerCase()} found in the document
                      </span>
                    </div>
                    <p className="text-xs text-foreground-muted">
                      The current value doesn't look like a real {friendlyFieldName(entity.label).toLowerCase()}. Remove it, or keep it if you know it's right.
                    </p>
                    <button
                      onClick={handleDeleteEntity}
                      disabled={isSubmitting}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-red-500/10 text-red-500 dark:text-red-400 hover:bg-red-500/20 border border-red-500/30 disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Remove value
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                    <span className="text-sm text-green-400">Current value is correct</span>
                  </div>
                )}
                {extractionResult.reasoning && (
                  <p className="text-xs text-foreground-muted mt-1">
                    {extractionResult.reasoning}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Edit mode
  return (
    <div className="p-3 bg-coral-500/10 border border-coral-500/30 rounded-lg space-y-3">
      {/* Corrected Text Input */}
      <div>
        <label className="block text-xs text-foreground-muted mb-1">Corrected Text:</label>
        <input
          type="text"
          value={correctedText}
          onChange={(e) => setCorrectedText(e.target.value)}
          className="w-full px-3 py-1.5 bg-coral-900/30 border border-coral-500/30 rounded text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-coral-500/50"
          placeholder="Enter correct value"
        />
      </div>

      {/* Label Selection - Grouped by Category */}
      <div>
        <label className="block text-xs text-foreground-muted mb-1">What kind of detail?</label>
        <select
          value={correctedLabel}
          onChange={(e) => setCorrectedLabel(e.target.value)}
          className="w-full px-3 py-1.5 bg-coral-900/30 border border-coral-500/30 rounded text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-coral-500/50"
        >
          {Object.entries(entityLabelsByCategory).map(([category, labels]) => (
            <optgroup key={category} label={category}>
              {labels.map((label: string) => (
                <option key={label} value={label}>{friendlyFieldName(label)}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Correction Reason (Optional) */}
      <div>
        <label className="block text-xs text-foreground-muted mb-1">Why? (Optional - helps learning):</label>
        <input
          type="text"
          value={correctionReason}
          onChange={(e) => setCorrectionReason(e.target.value)}
          className="w-full px-3 py-1.5 bg-coral-900/30 border border-coral-500/30 rounded text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-coral-500/50"
          placeholder="e.g., 'Missing leading zero' or 'Wrong date format'"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleSubmitCorrection}
          disabled={isSubmitting}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/40 text-green-300 rounded hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-wait"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" />
              <span className="text-sm">Submitting...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span className="text-sm">Submit Correction</span>
            </>
          )}
        </button>

        <button
          onClick={handleCancel}
          disabled={isSubmitting}
          className="px-3 py-1.5 bg-coral-500/10 border border-coral-500/30 text-foreground-muted rounded hover:bg-coral-500/20 transition-colors disabled:opacity-50"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Help Text */}
      <p className="text-xs text-foreground-muted">
        💡 Your correction helps the system learn and improves accuracy for future documents.
      </p>
    </div>
  );
};

export default EntityCorrectionWidget;
